import { useState } from 'react';

function generateId(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
}

const LOCATION_TYPES = [
  { value: 'committee_boat', label: 'Committee Boat' },
  { value: 'shore', label: 'Shore / Clubhouse' },
];

export default function StartLocationEditor({ locations, onChange }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: '', type: 'committee_boat', vhf: '' });

  const addLocation = () => {
    if (!draft.name.trim()) return;
    const id = generateId(draft.name);
    if (locations.some((l) => l.id === id)) return;
    const entry = {
      id,
      name: draft.name.trim(),
      type: draft.type,
    };
    if (draft.vhf.trim()) entry.vhf = draft.vhf.trim();
    onChange([...locations, entry]);
    setDraft({ name: '', type: 'committee_boat', vhf: '' });
    setAdding(false);
  };

  const removeLocation = (id) => {
    onChange(locations.filter((l) => l.id !== id));
  };

  const updateLocation = (id, field, value) => {
    onChange(locations.map((l) => {
      if (l.id !== id) return l;
      const next = { ...l };
      // Clear only on empty-string / null (delete the key) so falsy-but-valid
      // values (0, false) are preserved; an undefined value would otherwise
      // survive into the fleet_config setDoc and Firestore rejects it.
      if (value === '' || value == null) delete next[field];
      else next[field] = value;
      return next;
    }));
  };

  return (
    <section className="config-section">
      <h2>Start Locations</h2>
      <p className="section-hint">
        Define where starts happen (e.g. committee boat, hut, corinthian). Each location can have a VHF channel.
      </p>

      {locations.length === 0 && !adding && (
        <p className="empty-state">No start locations defined yet.</p>
      )}

      {locations.length > 0 && (
        <table className="config-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>ID</th>
              <th>Type</th>
              <th>VHF Channel</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {locations.map((loc) => (
              <tr key={loc.id}>
                <td>
                  <input
                    type="text"
                    value={loc.name}
                    onChange={(e) => updateLocation(loc.id, 'name', e.target.value)}
                    className="inline-input"
                  />
                </td>
                <td><code>{loc.id}</code></td>
                <td>
                  <select
                    value={loc.type || 'committee_boat'}
                    onChange={(e) => updateLocation(loc.id, 'type', e.target.value)}
                    className="inline-select"
                  >
                    {LOCATION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    value={loc.vhf || ''}
                    onChange={(e) => updateLocation(loc.id, 'vhf', e.target.value)}
                    className="inline-input vhf-input"
                    placeholder="e.g. 68"
                  />
                </td>
                <td>
                  <button className="btn btn-danger btn-sm" onClick={() => removeLocation(loc.id)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {adding ? (
        <div className="add-form">
          <div className="form-row">
            <input
              type="text"
              placeholder="Location name (e.g. National YC Hut)"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="inline-input"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && addLocation()}
            />
            <select
              value={draft.type}
              onChange={(e) => setDraft({ ...draft, type: e.target.value })}
              className="inline-select"
            >
              {LOCATION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="VHF (e.g. 68)"
              value={draft.vhf}
              onChange={(e) => setDraft({ ...draft, vhf: e.target.value })}
              className="inline-input vhf-input"
            />
            <button className="btn btn-primary btn-sm" onClick={addLocation}>Add</button>
            <button className="btn btn-sm" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn btn-sm" onClick={() => setAdding(true)}>+ Add Location</button>
      )}
    </section>
  );
}
